import { AdminRole } from "@repo/api-dtos"
import { SELLER_PROMOTION_CAMPAIGN_KINDS } from "@repo/shared-primitives"
import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router"
import { Plus, X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { QuickFilter } from "@/components/promotions/grid-view-actions"
import {
	PROMOTION_STATUS_LABELS,
	PROMOTION_STATUSES,
} from "@/components/promotions/promotion-helpers"
import { ToolbarSearch } from "@/components/promotions/toolbar-search"
import { Button } from "@/components/ui/button"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { CAMPAIGN_KIND_SHORT_LABELS } from "@/features/seller-promotions/campaign-labels"
import { SellerPromotionsAdminGrid } from "@/features/seller-promotions/SellerPromotionsAdminGrid"
import type { SellerPromotionsAdminInput } from "@/features/seller-promotions/seller-promotions-datasource"
import { useAdminRoles } from "@/hooks/use-admin-roles"

const MAX_SEARCH_LENGTH = 100

/** URL search params of the seller promotions list. */
export interface SellerPromotionsSearch {
	page?: number
	search?: string
	/** Column-managed filter (CSV-encoded values, owned by the grid). */
	status?: string
	/** Column-managed filter (CSV-encoded values, owned by the grid). */
	kind?: string
}

export const Route = createFileRoute("/seller-promotions/")({
	component: SellerPromotionsPage,
	validateSearch: (
		search: Record<string, unknown>,
	): SellerPromotionsSearch => ({
		page: normalizePage(search.page),
		search: normalizeSearch(search.search),
		status: search.status as string | undefined,
		kind: search.kind as string | undefined,
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) {
		return undefined
	}

	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function normalizeSearch(raw: unknown): string | undefined {
	if (typeof raw !== "string") {
		return undefined
	}

	return raw.trim().slice(0, MAX_SEARCH_LENGTH) || undefined
}

function SellerPromotionsPage() {
	const searchParams = useSearch({ from: "/seller-promotions/" })
	const navigate = useNavigate()
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceFinance)

	const pageIndex = (searchParams.page ?? 1) - 1
	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/seller-promotions",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: normalizeSearch(value), page: undefined })
		},
		[updateFilters],
	)

	const gridInput = React.useMemo<SellerPromotionsAdminInput>(
		() => (searchParams.search ? { search: searchParams.search } : {}),
		[searchParams.search],
	)

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/seller-promotions", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status || !!searchParams.kind || !!searchParams.search

	const toggleChip = React.useCallback(
		(key: "status" | "kind", value: string) => {
			updateFilters({
				[key]: searchParams[key] === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	const toolbar = (
		<>
			<ToolbarSearch>
				<DebouncedSearchInput
					defaultValue={searchParams.search ?? ""}
					onDebouncedChange={handleDebouncedSearch}
					placeholder="Search name or key"
					inputRef={searchRef}
					maxLength={MAX_SEARCH_LENGTH}
				/>
			</ToolbarSearch>

			<div className="flex items-center gap-1">
				{PROMOTION_STATUSES.map((status) => (
					<QuickFilter
						key={status}
						active={searchParams.status === status}
						onToggle={() => toggleChip("status", status)}
					>
						{PROMOTION_STATUS_LABELS[status]}
					</QuickFilter>
				))}
			</div>

			<div className="flex items-center gap-1">
				{SELLER_PROMOTION_CAMPAIGN_KINDS.map((kind) => (
					<QuickFilter
						key={kind}
						active={searchParams.kind === kind}
						onToggle={() => toggleChip("kind", kind)}
					>
						{CAMPAIGN_KIND_SHORT_LABELS[kind]}
					</QuickFilter>
				))}
			</div>

			{hasActiveFilters && (
				<Button variant="ghost" onClick={clearFilters}>
					<X />
					Clear
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Seller promotions"
				description="Reduced seller fees for sellers who connect an app, for every seller in a region, or for sellers you pick"
				actions={
					canOperate && (
						<Button asChild>
							<Link to="/seller-promotions/new">
								<Plus />
								Create promotion
							</Link>
						</Button>
					)
				}
			/>

			<div className="min-h-0 flex-1">
				<SellerPromotionsAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					toolbar={toolbar}
				/>
			</div>
		</div>
	)
}
